// EmojiInput — input de texto + botão 😀 que abre picker de emoji nativo
// (web component emoji-picker-element). Resolve a dor de ter que abrir caçar
// emoji em outro lugar pra preencher campo Icon (page/coleção/home_card).
//
// Uso:
//   <EmojiInput value={icon} onChange={setIcon} placeholder="📊" />
//
// Atalho do sistema também funciona normal no input: Cmd+Ctrl+Espaço (macOS),
// Win+. (Windows). O botão é fallback / discoverable para quem não conhece.

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Smile } from 'lucide-react';
import 'emoji-picker-element';

// Web component declaration pra TS aceitar <emoji-picker> em JSX
declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'emoji-picker': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        class?: string;
      };
    }
  }
}

interface Props {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  title?: string;
  /** Largura do botão (default size=14) */
  buttonSize?: number;
  /** Quando true, esconde o input texto e mostra só o botão (raro). Default false. */
  hideInput?: boolean;
}

const PICKER_WIDTH = 360;
const PICKER_HEIGHT = 420;

export function EmojiInput({
  value,
  onChange,
  placeholder,
  className,
  inputClassName,
  title,
  buttonSize = 14,
  hideInput = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const pickerWrapRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  // Posicionar popover quando abre. Recalcula em resize/scroll.
  useEffect(() => {
    if (!open) return;
    const place = () => {
      if (!btnRef.current) return;
      const rect = btnRef.current.getBoundingClientRect();
      let left = rect.left;
      let top = rect.bottom + 4;
      if (left + PICKER_WIDTH > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - PICKER_WIDTH - 8);
      }
      if (top + PICKER_HEIGHT > window.innerHeight - 8) {
        // Não cabe embaixo → abrir pra cima
        top = Math.max(8, rect.top - PICKER_HEIGHT - 4);
      }
      setPos({ top, left });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  // Listener do emoji-click — o web component dispara CustomEvent('emoji-click')
  // com detail.unicode contendo o emoji selecionado.
  useEffect(() => {
    if (!open || !pickerWrapRef.current) return;
    const picker = pickerWrapRef.current.querySelector('emoji-picker') as HTMLElement | null;
    if (!picker) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && typeof detail.unicode === 'string') {
        onChange(detail.unicode);
        setOpen(false);
      }
    };
    picker.addEventListener('emoji-click', handler);
    return () => picker.removeEventListener('emoji-click', handler);
  }, [open, onChange]);

  // Click fora fecha. ESC também.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (pickerWrapRef.current?.contains(target)) return;
      if (btnRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={`inline-flex items-center gap-1 ${className ?? ''}`}>
      {!hideInput && (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={inputClassName}
          title={title}
        />
      )}
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className="text-gray-400 hover:text-indigo-600 transition-colors inline-flex items-center justify-center px-1.5 py-1 rounded hover:bg-gray-100"
        title="Abrir picker de emoji (ou Cmd+Ctrl+Espaço)"
        aria-label="Abrir picker de emoji"
      >
        <Smile size={buttonSize} />
      </button>
      {open &&
        createPortal(
          <div
            ref={pickerWrapRef}
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              zIndex: 9999,
              boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            {/* @ts-expect-error custom element from emoji-picker-element */}
            <emoji-picker
              style={
                {
                  width: `${PICKER_WIDTH}px`,
                  height: `${PICKER_HEIGHT}px`,
                  '--background': '#fff',
                  '--border-radius': '8px',
                } as React.CSSProperties
              }
            />
          </div>,
          document.body,
        )}
    </div>
  );
}
